import { useCallback, useEffect, useState } from 'react';
import { socialApi } from '@/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { dataTablePageProps, usePagination } from '@/hooks/usePagination';
import type { SocialListeningMatch } from '@/types/social';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';

export function SocialListeningPage() {
  const pagination = usePagination();
  const [matches, setMatches] = useState<SocialListeningMatch[]>([]);
  const [keyword, setKeyword] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await socialApi.listListeningMatches({
        page: pagination.page,
        size: pagination.size,
      });
      setMatches(res.content ?? []);
      pagination.applyPageResponse(res);
    } catch (err) {
      showApiError(err, 'Listening unavailable on current plan');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.size, pagination]);

  useEffect(() => {
    load();
  }, [load]);

  const columns: Column<SocialListeningMatch>[] = [
    { key: 'platform', header: 'Platform' },
    { key: 'matchType', header: 'Type' },
    { key: 'contentPreview', header: 'Preview', render: (r) => r.contentPreview?.slice(0, 120) ?? '—' },
    {
      key: 'matchedAt',
      header: 'Matched',
      render: (r) => (r.matchedAt ? new Date(r.matchedAt).toLocaleString() : '—'),
    },
  ];

  const addKeyword = async () => {
    if (!keyword.trim()) return;
    try {
      await socialApi.addListeningKeyword({ keywordValue: keyword.trim(), keywordType: 'KEYWORD' });
      notify.success('Keyword added');
      setKeyword('');
      load();
    } catch (err) {
      showApiError(err, 'Add keyword failed');
    }
  };

  return (
    <div>
      <PageHeader
        title="Social Listening"
        description="Brand, keyword, and hashtag matches across connected channels"
        breadcrumbs={[{ label: 'Org', to: '/org' }, { label: 'Social', to: '/social' }, { label: 'Listening' }]}
      />
      <div className="card mb-4 flex flex-wrap gap-2 p-4 items-end">
        <Input label="Add keyword" value={keyword} onChange={(e) => setKeyword(e.target.value)} className="flex-1 min-w-[200px]" />
        <Button onClick={() => void addKeyword()}>Add</Button>
      </div>
      {!loading && matches.length === 0 ? (
        <div className="card p-6 text-sm text-slate-500">
          No matches yet. Add keywords in Setup — matches appear as platforms sync or simulate inbound activity.
        </div>
      ) : (
        <DataTable
          columns={columns}
          data={matches}
          keyField="id"
          loading={loading}
          {...dataTablePageProps(pagination)}
        />
      )}
    </div>
  );
}
